import { Application } from "./constants/common";

// Values the app keeps in the browser. Each kind of storage holds one item named after the app
// (Application.StorageKeys.SessionStorage and .LocalStorage): an object with one entry per key,
// so the app adds a single item to each. Session storage lasts while the tab is open, local
// storage from one visit to the next. Entry names live in Application.StorageKeys.

/**
 * The value kept under the key for this tab's session. With no key, every entry; null when
 * nothing has been kept yet.
 * @param {string} [key]
 */
export const retrieveSessionStorage = (key) => {
  const entries = JSON.parse(sessionStorage.getItem(Application.StorageKeys.SessionStorage));
  const hasEntries = !!entries && Object.keys(entries).length > 0;

  return hasEntries && key ? entries[key] : entries;
};

/**
 * Keeps a value under the key for this tab's session, beside the entries already kept.
 * @param {string} key
 * @param {unknown} value
 */
export const saveSessionStorage = (key, value) => {
  const entries = retrieveSessionStorage() || {};

  entries[key] = value;
  sessionStorage.setItem(Application.StorageKeys.SessionStorage, JSON.stringify(entries));
};

// Local storage can be unavailable (private mode, blocked by policy): a read then answers with
// its fallback and a write lasts for this page load only.
const readLocalEntries = () => {
  try {
    const entries = JSON.parse(window.localStorage.getItem(Application.StorageKeys.LocalStorage));

    return entries !== null && typeof entries === "object" && !Array.isArray(entries) ? entries : {};
  } catch {
    return {};
  }
};

/**
 * The value kept under the key from one visit to the next, or the fallback when nothing is kept
 * there or storage is unavailable.
 * @template T
 * @param {string} key
 * @param {T} fallback
 * @returns {T}
 */
export const retrieveLocalStorage = (key, fallback) => {
  const entries = readLocalEntries();

  return key in entries ? entries[key] : fallback;
};

/**
 * Keeps a value under the key from one visit to the next, beside the entries already kept.
 * @param {string} key
 * @param {unknown} value
 */
export const saveLocalStorage = (key, value) => {
  try {
    window.localStorage.setItem(Application.StorageKeys.LocalStorage, JSON.stringify({ ...readLocalEntries(), [key]: value }));
  } catch {
    // Storage unavailable: the value is kept by the caller's own state for this page load.
  }
};
